'use client';

import { Box, freddyTheme, Modal, ModalBody, Tag, Typography } from '@coolblue-development/freddy';
import selectContentTriggerPromoIcons from '@coolblue-development/ga4-events/triggers/PromoIcons/selectContentTrigger';
import showPromoIconTrigger from '@coolblue-development/ga4-events/triggers/PromoIcons/showPromoIconTrigger';
import readMoreTrigger from '@coolblue-development/ga4-events/triggers/PromoLabels/readMoreTrigger';
import selectContentTriggerPromoLabels from '@coolblue-development/ga4-events/triggers/PromoLabels/selectContentTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { Image } from '@coolblue-development/next-image/client';
import { PersonalPrice, PromotionalContent } from '@features/product/typings';
import { useIsLeasePage } from '@hooks/useIsLeasePage';
import { useTranslation } from '@i18n/useTranslation';
import { promotionalContentFactory } from '@utils/promotionalContent/promotionalContentFactory';
import { Fragment, ReactElement, useEffect, useRef, useState } from 'react';

type PromotionalContentIconsProps = {
  promotionalContent?: PromotionalContent;
  productName: string;
  personalPrice?: PersonalPrice;
};

export default function PromotionalContentIcons({
  promotionalContent,
  productName,
  personalPrice
}: PromotionalContentIconsProps): ReactElement[] {
  const isLeasePage = useIsLeasePage();
  const { trackEvent } = useAnalytics();
  const [promoLabelModalIsOpen, setPromoLabelModalIsOpen] = useState(false);
  const [promoIconModalIsOpen, setPromoIconModalIsOpen] = useState(false);
  const promoIcons: ReactElement[] = [];
  const labelRef = useRef<HTMLDivElement | null>(null);
  const iconRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation('webshop');
  const modalTranslations = useFreddyTranslations('modal');
  const hasPersonalDiscount = !!personalPrice?.labelText;

  const mappedContent = promotionalContentFactory(promotionalContent);

  useEffect(() => {
    if (
      (!labelRef.current && promoLabelModalIsOpen) ||
      (!iconRef.current && promoIconModalIsOpen)
    ) {
      return;
    }

    const modalBody = labelRef.current ?? iconRef.current;
    const links = modalBody?.querySelectorAll('a');

    if (!links) {
      return;
    }

    const handleClick = () => {
      trackEvent(
        readMoreTrigger({
          subject_type: mappedContent?.promoLabel ? 'label' : 'item',
          subject_id: mappedContent?.promoLabel
            ? (mappedContent?.promoLabel?.label ?? '')
            : (mappedContent?.promoIcon?.image.id ?? '')
        })
      );
    };

    links.forEach(link => link.addEventListener('click', handleClick));

    return () => {
      links.forEach(link => link.removeEventListener('click', handleClick));
    };
  }, [
    mappedContent?.promoLabel,
    mappedContent?.promoIcon?.image.id,
    promoIconModalIsOpen,
    trackEvent,
    promoLabelModalIsOpen
  ]);

  function handlePromoLabelClick() {
    const hasLabel = Boolean(mappedContent?.promoLabel?.label);

    if (hasLabel) {
      setPromoLabelModalIsOpen(true);
      trackEvent(
        selectContentTriggerPromoLabels({
          content: mappedContent?.promoLabel?.label ?? ''
        })
      );
    }
  }

  function handlePromoIconClick() {
    const hasIconId = Boolean(mappedContent?.promoIcon?.image?.id);

    if (hasIconId) {
      setPromoIconModalIsOpen(true);
      trackEvent(
        selectContentTriggerPromoIcons({
          content: mappedContent?.promoIcon?.image.id ?? ''
        })
      );
    }
  }

  const labelText = (personalPrice?.labelText ?? mappedContent?.promoLabel?.label ?? '').trim();

  if (labelText) {
    const hasExplanation = !!mappedContent?.promoLabel?.details?.explanation;

    promoIcons.push(
      <Fragment key={labelText}>
        <TrackImmediate trigger={showPromoIconTrigger()} />
        <Tag
          dangerouslySetInnerHTML={{
            __html: labelText
          }}
          onClick={() => hasExplanation && !hasPersonalDiscount && handlePromoLabelClick()}
          size='large'
          variant='subtle'
          component={hasExplanation && !hasPersonalDiscount ? 'button' : 'span'}
        />
        {hasExplanation && !hasPersonalDiscount && !isLeasePage && (
          <Modal
            open={promoLabelModalIsOpen}
            onClose={() => setPromoLabelModalIsOpen(false)}
            size='medium'
            title={mappedContent?.promoLabel?.details?.title}
            translations={modalTranslations}
            body={
              <ModalBody>
                <Typography
                  ref={labelRef}
                  dangerouslySetInnerHTML={{
                    __html: mappedContent?.promoLabel?.details?.explanation || ''
                  }}
                />
              </ModalBody>
            }
          />
        )}
      </Fragment>
    );
  }

  if (mappedContent?.promoIcon?.image?.id) {
    const hasExplanation = !!mappedContent?.promoIcon.details?.explanation;

    promoIcons.push(
      <Fragment key={mappedContent?.promoIcon.image.id}>
        <TrackImmediate trigger={showPromoIconTrigger()} />
        <Box
          component={hasExplanation ? 'button' : 'span'}
          onClick={() => hasExplanation && handlePromoIconClick()}
          sx={{
            cursor: hasExplanation ? 'pointer' : 'default',
            background: 'none',
            border: 'none',
            padding: 0,
            display: 'flex',
            '& img': {
              width: 'auto',
              maxWidth: '8.8rem',
              maxHeight: '10rem',
              [freddyTheme.breakpoint.minWidth('sm')]: {
                maxHeight: '12rem'
              }
            }
          }}
        >
          <Image
            imageId={mappedContent.promoIcon?.image.id}
            fileCollection={mappedContent.promoIcon?.image.collectionName}
            width={88}
            height={80}
            fullHeight
            alt={t('mediaGallery.promotionalLabelForProduct', { productName })}
            loading='eager'
            background='transparent'
          />
        </Box>
        {hasExplanation && !isLeasePage && (
          <Modal
            open={promoIconModalIsOpen}
            onClose={() => setPromoIconModalIsOpen(false)}
            size='medium'
            translations={modalTranslations}
            title={mappedContent?.promoIcon.details?.title}
            body={
              <ModalBody>
                <Typography
                  ref={iconRef}
                  dangerouslySetInnerHTML={{
                    __html: mappedContent?.promoIcon.details?.explanation || ''
                  }}
                />
              </ModalBody>
            }
          />
        )}
      </Fragment>
    );
  }

  if (isLeasePage) {
    return [];
  }

  return promoIcons;
}
